import { InputRef, PORTAL_CLASS_NAME, useNavItemsNavigation } from '@billy/design-system'
import isEmpty from 'lodash/isEmpty'
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useClickAway, useKey } from 'react-use'

import { useUserUmbrellas } from '@modules-deprecated/app/umbrellas'
import { useOrganizations } from '@views/settings/routes/UserOrganizationsSettings/hooks/useOrganizations'
import { useOrganizationsTableData } from '@views/settings/routes/UserOrganizationsSettings/hooks/useOrganizationsTableData'
import { OrganizationsTableRow } from '@views/settings/routes/UserOrganizationsSettings/types/OrganizationsTableRow'

import { useOrganizationsSearch } from '../../contexts/organizationsSearchContext'
import { Timeout } from '../../types/timeout'
import { OrganizationAvatar } from '../Navigation/elements/OrganizationAvatar'
import { UmbrellaAvatar } from '../Navigation/elements/UmbrellaAvatar'
import { organizationsSearchTriggerDataId } from './constants/triggerDataId'
import { OrganizationsSearchInput } from './elements/OrganizationsSearchInput'
import { OrganizationsSearchResults } from './elements/OrganizationsSearchResults'
import * as Styled from './styles'
import { SearchResultItem } from './types/searchResultItem'

const SEARCH_CLOSE_ANIMATION_TIMEOUT_MS = 250

export const OrganizationsSearch = () => {
  const { isOpen, isRendered, setIsOpen } = useOrganizationsSearch()
  const [searchValue, setSearchValue] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<InputRef>(null)
  const closeTimeout = useRef<Timeout>()

  const { organizations, isLoading } = useOrganizations()
  const { userUmbrellas } = useUserUmbrellas()
  const tableData = useOrganizationsTableData(organizations)

  const organizationsResults: OrganizationsTableRow[] = useMemo(() => {
    return searchValue
      ? tableData.filter((organization) => organization.name.toLowerCase().includes(searchValue.toLowerCase()))
      : tableData
  }, [searchValue, tableData])

  const userUmbrellasResults = useMemo(() => {
    return userUmbrellas.filter((umbrella) => umbrella.name.toLowerCase().includes(searchValue.toLowerCase()))
  }, [searchValue, userUmbrellas])

  const userUmbrellasItems: SearchResultItem[] = useMemo(
    () =>
      userUmbrellasResults.map((result) => ({
        id: result.umbrellaId,
        text: result.name,
        href: `/umbrellas/${result.umbrellaId}`,
        icon: <UmbrellaAvatar logoId={result.logoFileId} />,
      })),
    [userUmbrellasResults],
  )
  const organizationItems: SearchResultItem[] = useMemo(
    () =>
      organizationsResults.map((result) => ({
        id: result.id,
        text: result.name,
        href: `/${result.url}`,
        icon: <OrganizationAvatar organizationId={result.id} organizationName={result.name} src={result.iconUrl} />,
      })),
    [organizationsResults],
  )

  useEffect(() => {
    let requestAnimationId: number

    if (isOpen) {
      // wait a frame so there is no initial shortcut text inside the search input
      requestAnimationId = window.requestAnimationFrame(() => {
        inputRef?.current?.focus()
      })
      return
    }

    inputRef?.current?.blur()

    return () => {
      if (requestAnimationId) {
        window.cancelAnimationFrame(requestAnimationId)
      }

      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current)
      }
    }
  }, [isOpen])

  const close = useCallback(() => {
    setIsOpen(false)

    closeTimeout.current = setTimeout(() => {
      setSearchValue('')
    }, SEARCH_CLOSE_ANIMATION_TIMEOUT_MS)
  }, [setIsOpen])

  const clearInputThenClose = useCallback(() => {
    if (searchValue) {
      setSearchValue('')
      return
    }

    setIsOpen(false)
  }, [searchValue, setIsOpen])

  const handleClickAway = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const searchTriggerElements = document.querySelectorAll(`[data-id='${organizationsSearchTriggerDataId}']`)

      const hasClickedOnTrigger = Array.from(searchTriggerElements).some(
        (element) => element.contains(target) || element === target,
      )

      if (hasClickedOnTrigger) {
        return
      }

      close()
    },
    [close],
  )

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchValue(value)
  }, [])

  const navigateToItem = useCallback((item: SearchResultItem) => {
    if (item.href) {
      window.location.href = item.href // Force reload to reset all states
    }
  }, [])

  const handleSearchItemClick = useCallback(
    (item: SearchResultItem) => {
      navigateToItem(item)

      if (!isEmpty(item)) {
        close()
      }
    },
    [close, navigateToItem],
  )

  // Shortcuts

  useKey('Escape', clearInputThenClose, undefined, [searchValue])
  useClickAway(wrapperRef, handleClickAway)

  const focusedId = useNavItemsNavigation({
    items: [userUmbrellasItems, organizationItems].flat(),
    isActive: isOpen,
  })

  if (!isRendered) {
    return null
  }

  return ReactDOM.createPortal(
    <Styled.OrganizationsSearchWrapper className={PORTAL_CLASS_NAME} ref={wrapperRef} visible={isOpen}>
      <OrganizationsSearchInput
        isLoading={isLoading}
        onChange={handleInputChange}
        onClose={close}
        ref={inputRef}
        value={searchValue}
      />
      <OrganizationsSearchResults
        organizationItems={organizationItems}
        umbrellaItems={userUmbrellasItems}
        onItemClick={handleSearchItemClick}
        searchValue={searchValue}
        focusedId={focusedId}
      />
    </Styled.OrganizationsSearchWrapper>,
    document.body,
  )
}
